import { useCallback, useEffect, useState } from 'react';

/*
 * useFullscreen — toggle the Fullscreen API on a target element (default: the
 * document root). Must be called from a user gesture. Best-effort landscape
 * orientation lock on mobile (ignored where unsupported).
 */

interface OrientationLock extends ScreenOrientation {
  lock?: (orientation: 'landscape' | 'portrait' | 'any') => Promise<void>;
}

// Exit fullscreen and release any orientation lock. Standalone (no hook state)
// so it can run from cleanup paths like leaving the game page. Safe to call when
// not in fullscreen. Releasing the lock matters on mobile: entering fullscreen
// locks landscape, and without unlocking the phone stays rotated afterwards.
export async function exitFullscreen(): Promise<void> {
  try {
    // `unlock` isn't in every lib.dom version; guard via cast.
    (window.screen?.orientation as { unlock?: () => void } | undefined)?.unlock?.();
    if (document.fullscreenElement) {
      await document.exitFullscreen?.();
    }
  } catch {
    /* ignore */
  }
}

export function useFullscreen(target?: () => Element | null) {
  const [isFullscreen, setIsFullscreen] = useState(
    () => typeof document !== 'undefined' && !!document.fullscreenElement,
  );

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const enter = useCallback(async () => {
    const el = target?.() ?? document.documentElement;
    try {
      await el.requestFullscreen?.();
      const orientation = window.screen?.orientation as OrientationLock | undefined;
      await orientation?.lock?.('landscape').catch(() => undefined);
    } catch {
      /* denied or unsupported — ignore */
    }
  }, [target]);

  const exit = useCallback(() => exitFullscreen(), []);

  const toggle = useCallback(() => {
    if (document.fullscreenElement) void exit();
    else void enter();
  }, [enter, exit]);

  return { isFullscreen, enter, exit, toggle };
}
